/**
 * Отбор и представления.
 *
 * Дерево условий здесь — тот же Filter DSL, что и на сервере: клиент его
 * собирает и показывает, но не толкует. Проверка одна и живёт на сервере,
 * поэтому расхождению взяться неоткуда.
 */

import { downloadFile, request } from "@/shared/api/client";
import type { TaskPage, TaskRow } from "@/shared/api/tasks";

export interface Condition {
  field: string;
  op: string;
  value?: unknown;
}

export interface Group {
  op: "and" | "or" | "not";
  items: FilterNode[];
}

export type FilterNode = Condition | Group;

export function isGroup(node: FilterNode): node is Group {
  return "items" in node;
}

/** Вид значения поля: от него зависит, чем вводить значение. */
export type FieldKind = "number" | "text" | "date" | "boolean" | "reference" | "array";

export interface FilterField {
  name: string;
  title: string;
  kind: FieldKind;
  operators: string[];
  group: string;
  assignable: boolean;
}

export interface FilterVocabulary {
  fields: FilterField[];
  operators: { key: string; title: string; arity: string }[];
  variables: { key: string; title: string }[];
}

export function filterVocabulary(objectId?: number): Promise<FilterVocabulary> {
  const query = objectId === undefined ? "" : `?object_id=${objectId}`;
  return request<FilterVocabulary>(`/api/filter-fields${query}`);
}

export interface SearchRequest {
  filter?: FilterNode | null;
  conditions?: Condition[];
  logic?: string | null;
  q?: string;
  trash?: boolean;
  sort?: string;
  ascending?: boolean;
  limit?: number;
  cursor?: string | null;
}

export function searchTasks(body: SearchRequest): Promise<TaskPage> {
  return request<TaskPage>("/api/tasks/search", { method: "POST", body });
}

export function countSearch(body: SearchRequest): Promise<{ count: number }> {
  return request<{ count: number }>("/api/tasks/search/count", { method: "POST", body });
}

// ── Представления ────────────────────────────────────────────────────────────

export type ViewScope = "personal" | "shared" | "account";

export type ViewMode =
  "table" | "list" | "kanban" | "planner" | "calendar" | "calendar_hours" | "gantt";

export const VIEW_MODE_TITLES: Record<ViewMode, string> = {
  table: "Таблица",
  list: "Список",
  kanban: "Канбан",
  planner: "Планировщик",
  calendar: "Календарь",
  calendar_hours: "Почасовой календарь",
  gantt: "Гантт",
};

export interface ViewColumn {
  field: string;
  width?: number;
}

export interface ViewSort {
  field: string;
  direction: "asc" | "desc";
}

export interface SavedView {
  id: number;
  name: string;
  owner_id: number;
  scope: ViewScope;
  mode: ViewMode;
  entity: string;
  filter: Record<string, unknown>;
  /** Тот же отбор правилами по номерам — то, что показывает редактор. */
  conditions: Condition[];
  logic: string;
  columns: ViewColumn[];
  grouping: Record<string, unknown>;
  sort: ViewSort[];
  settings: Record<string, unknown>;
  is_default: boolean;
  object_id: number | null;
  position: number;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
}

export interface ViewDraft {
  name: string;
  scope?: ViewScope;
  mode?: ViewMode;
  entity?: string;
  filter?: FilterNode | null;
  /** Альтернативная запись отбора: правила плюс «(1 И 2) ИЛИ 3». */
  conditions?: Condition[];
  logic?: string;
  columns?: ViewColumn[];
  grouping?: Record<string, unknown> | null;
  sort?: ViewSort[];
  settings?: Record<string, unknown>;
  object_id?: number | null;
  is_default?: boolean;
}

export function listViews(): Promise<SavedView[]> {
  return request<SavedView[]>("/api/views");
}

export function createView(draft: ViewDraft): Promise<SavedView> {
  return request<SavedView>("/api/views", { method: "POST", body: draft });
}

export function updateView(viewId: number, patch: Partial<ViewDraft>): Promise<SavedView> {
  return request<SavedView>(`/api/views/${viewId}`, { method: "PATCH", body: patch });
}

export function deleteView(viewId: number): Promise<void> {
  return request<undefined>(`/api/views/${viewId}`, { method: "DELETE" });
}

export function shareView(viewId: number, principalId: number, canEdit = false): Promise<void> {
  return request<undefined>(`/api/views/${viewId}/access`, {
    method: "POST",
    body: { principal_id: principalId, can_edit: canEdit },
  });
}

export function unshareView(viewId: number, principalId: number): Promise<void> {
  return request<undefined>(`/api/views/${viewId}/access/${principalId}`, {
    method: "DELETE",
  });
}

export function viewTasks(
  viewId: number,
  page: { limit?: number; cursor?: string | null } = {},
): Promise<TaskPage> {
  const params = new URLSearchParams();
  params.set("limit", String(page.limit ?? 50));
  if (page.cursor) {
    params.set("cursor", page.cursor);
  }
  return request<TaskPage>(`/api/views/${viewId}/tasks?${params.toString()}`);
}

// ── Доска ────────────────────────────────────────────────────────────────────

export interface BoardColumn {
  value: unknown;
  total: number;
  items: TaskRow[];
  has_more: boolean;
}

export interface Board {
  column_field: string;
  columns: BoardColumn[];
}

export interface BoardRequest extends SearchRequest {
  column_field: string;
  /** Порядок колонок: его задаёт тот, кто знает, как их показывать. */
  values: unknown[];
  per_column?: number;
}

export function taskBoard(body: BoardRequest): Promise<Board> {
  return request<Board>("/api/tasks/board", { method: "POST", body });
}

/** Перенос карточки — присваивание значения тому полю, по которому разложена доска. */
export function moveCard(taskId: number, columnField: string, value: unknown): Promise<unknown> {
  return request<unknown>(`/api/tasks/${taskId}/card-move`, {
    method: "POST",
    body: { column_field: columnField, value },
  });
}

/** Выгрузка представления: тот же отбор и те же колонки, что на экране. */
export function exportView(viewId: number, format: "csv" | "xlsx"): Promise<void> {
  return downloadFile(`/api/views/${viewId}/export?format=${format}`);
}
